import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { adminApi, errMsg, eurNum } from "@/lib/proApi";

const CONTACT = { mail: "pchaubo@hotmail.fr", wa: "06 25 99 55 02", site: "ozentya.org/espace-pro" };
const CLIENT_FIELDS = ["Raison sociale", "Nom du contact", "SIRET", "N° TVA intracom.", "Téléphone", "E-mail",
  "Adresse de livraison", "Code postal / Ville / Pays"];
const PAGE_PX = 1040;

const Line = ({ label }) => (
  <div className="flex items-end gap-2 text-[10px]">
    <span className="whitespace-nowrap text-neutral-600">{label}</span>
    <span className="flex-1 border-b border-dotted border-neutral-400 h-3.5" />
  </div>
);

const ttc = (ht, vat) => Math.round(ht * (1 + vat / 100) * 100) / 100;

const Row = ({ p, vat }) => (
  <tr data-testid={`order-form-row-${p.slug}`} className="border-b border-neutral-300 align-middle">
    <td className="py-1.5 pr-2">
      <div className="text-[11px] font-semibold leading-tight">{p.name}</div>
      <div className="text-[8px] uppercase tracking-wide text-neutral-500 leading-tight">{p.category}</div>
    </td>
    <td data-testid={`order-form-ht-${p.slug}`} className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">{eurNum(p.unit_price_ht)}</td>
    <td data-testid={`order-form-ttc-${p.slug}`} className="py-1.5 px-2 text-right text-[11px] font-semibold whitespace-nowrap">{eurNum(ttc(p.unit_price_ht, vat))}</td>
    <td className="py-1 px-2"><div className="mx-auto h-5 w-16 border border-neutral-500" /></td>
    <td className="py-1 pl-2"><div className="ml-auto h-5 w-24 border border-neutral-500" /></td>
  </tr>
);

const Conditions = ({ s }) => (
  <div data-testid="order-form-conditions" className="grid grid-cols-2 gap-4 text-[8.5px] leading-snug mt-2">
    <div>
      <h3 className="font-semibold text-[9.5px] mb-0.5">Livraison</h3>
      <ul className="list-disc pl-3">
        <li>France métropolitaine : <b>offerte</b> dès 300 € HT d'achat (transporteur ou Mondial Relay), sous 10 à 15 jours. En dessous, retrait au dépôt Ozentya, 5 rue des Champs Rogers, 78400 Chatou.</li>
        <li>DOM-TOM (971 à 976, 984 à 988) : 30 € TTC jusqu'à 150 € HT, 40 € TTC de 150 à 400 € HT, 60 € TTC à partir de 400 € HT.</li>
        <li>Europe (Belgique et autres pays) : 50 € par transporteur.</li>
        <li>Minimum 12 pièces par produit. Paiement possible en 4 fois avec PayPal ou avec Klarna.</li>
      </ul>
    </div>
    <div>
      <h3 className="font-semibold text-[9.5px] mb-0.5">Paiement</h3>
      <p>{s?.payment_terms}</p>
      {s?.bank?.iban && <p className="mt-0.5">Virement : {s.bank.holder} — IBAN {s.bank.iban} — BIC {s.bank.bic}</p>}
      <p className="mt-0.5">Prix unitaires en euros, HT et TTC (TVA {String(s?.vat_rate ?? 20).replace(".", ",")} %). Tarifs susceptibles d'évoluer ; seule la facture fait foi.</p>
    </div>
  </div>
);

const Totals = () => (
  <div className="mt-2 grid grid-cols-2 gap-6">
    <div className="rounded border border-neutral-400 p-1.5 text-[9px] h-16">
      <div className="font-semibold">Date, cachet et signature du client</div>
    </div>
    <div className="space-y-1 text-[10px]">
      {["Total produits TTC", "Livraison", "Total à payer TTC"].map((l) => <Line key={l} label={l} />)}
    </div>
  </div>
);

const useFitOnePage = (ref) => useEffect(() => {
  const fit = () => {
    const el = ref.current;
    if (!el) return;
    el.parentNode.style.display = "block";
    Object.assign(el.style, { zoom: "", width: "718px", maxWidth: "none", padding: "0" });
    const h = el.scrollHeight;
    if (h > PAGE_PX) el.style.zoom = String(Math.floor((PAGE_PX / h) * 100) / 100);
  };
  const reset = () => {
    if (!ref.current) return;
    ref.current.parentNode.style.display = "";
    Object.assign(ref.current.style, { zoom: "", width: "", maxWidth: "", padding: "" });
  };
  window.addEventListener("beforeprint", fit);
  window.addEventListener("afterprint", reset);
  return () => { window.removeEventListener("beforeprint", fit); window.removeEventListener("afterprint", reset); };
}, [ref]);

const Sheet = React.forwardRef(({ products, settings, testid }, ref) => {
  const seller = settings?.seller || {};
  return (
    <div ref={ref} data-testid={testid} className="oz-print mx-auto max-w-[210mm] bg-white p-[10mm] shadow text-neutral-900">
      <header className="flex justify-between items-start border-b-2 border-neutral-900 pb-1.5">
        <div>
          <div className="font-serif text-2xl leading-none">Ozentya<span className="text-amber-600">.</span></div>
          <div className="text-[8.5px] text-neutral-600 mt-1">{seller.name} — {seller.address}, {seller.postal_city} — SIRET {seller.siret} — TVA {seller.vat_number}</div>
        </div>
        <div className="text-right">
          <h1 data-testid="order-form-title" className="text-base font-bold uppercase tracking-wide leading-tight">Bon de commande professionnel</h1>
          <div className="text-[8.5px] text-neutral-600">Tarifs HT au {new Date().toLocaleDateString("fr-FR")}</div>
          <div className="text-[10px] mt-0.5">N° ______________ &nbsp; Date : ___ / ___ / ______</div>
        </div>
      </header>
      <section className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2">
        {CLIENT_FIELDS.map((f) => <Line key={f} label={f} />)}
      </section>
      <p className="mt-1.5 text-[8.5px] text-neutral-600">Indiquez le <b>nombre de pièces</b> souhaitées, puis le total : prix unitaire TTC × nombre de pièces.</p>
      <table data-testid="order-form-table" className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-neutral-900 text-[8.5px] uppercase tracking-wide">
            <th className="py-1 text-left">Produit</th><th className="py-1 px-2 text-right">Prix unit. HT</th>
            <th className="py-1 px-2 text-right">Prix unit. TTC</th><th className="py-1 px-2 text-center">Nombre de pièces</th>
            <th className="py-1 pl-2 text-right">Total TTC</th>
          </tr>
        </thead>
        <tbody>{products.map((p) => <Row key={p.id} p={p} vat={settings?.vat_rate ?? 20} />)}</tbody>
      </table>
      <Totals />
      <Conditions s={settings} />
      <footer className="mt-2 border-t border-neutral-300 pt-1 text-[8.5px] text-center text-neutral-600">
        Bon à retourner par e-mail à {CONTACT.mail} ou par WhatsApp au {CONTACT.wa} — Commande en ligne : {CONTACT.site}
      </footer>
    </div>
  );
});

export default function AdminOrderForm() {
  const [products, setProducts] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const sheet = useRef(null);
  useFitOnePage(sheet);
  useEffect(() => {
    Promise.all([adminApi.get("/admin/pro/products"), adminApi.get("/admin/pro/settings")])
      .then(([p, s]) => { setProducts(p.data.filter((x) => x.active)); setSettings(s.data); })
      .catch((e) => setError(errMsg(e)));
  }, []);
  return (
    <div data-testid="admin-order-form-page" className="bg-neutral-100 min-h-screen py-8 print:bg-white print:py-0">
      <div className="mx-auto max-w-[210mm] px-4 mb-4 flex items-center justify-between print:hidden">
        <Link to="/admin" data-testid="order-form-back" className="inline-flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Admin</Link>
        <button data-testid="order-form-print-btn" onClick={() => window.print()} disabled={!products}
          className="inline-flex items-center gap-2 rounded-full bg-neutral-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-neutral-700 transition-colors disabled:opacity-50">
          <Printer size={16} /> Imprimer / PDF
        </button>
      </div>
      {error && <p data-testid="order-form-error" className="text-center text-red-600 text-sm">{error} — connectez-vous d'abord sur /admin.</p>}
      {!products && !error && <p className="text-center text-sm">Chargement…</p>}
      {products && (
        <Sheet products={products} settings={settings} testid="order-form-sheet" />
      )}
      {products && createPortal(
        <div className="oz-print-root"><Sheet ref={sheet} products={products} settings={settings} testid="order-form-print-sheet" /></div>,
        document.body)}
    </div>
  );
}
